export interface DotsResult {
  dots: boolean[][];
  brailleLines: string[];
  brailleColumns?: string[][];
  width: number;
  height: number;
}

// 小書き文字（拗音・促音など）
const SMALL_KANA = new Set([
  // ひらがな小書き
  'ぁ', 'ぃ', 'ぅ', 'ぇ', 'ぉ',
  'ゃ', 'ゅ', 'ょ',
  'っ',
  'ゎ', 'ゕ', 'ゖ',
  // カタカナ小書き
  'ァ', 'ィ', 'ゥ', 'ェ', 'ォ',
  'ャ', 'ュ', 'ョ',
  'ッ',
  'ヮ', 'ヵ', 'ヶ',
  // 追加の小書き
  'ㇰ', 'ㇱ', 'ㇲ', 'ㇳ', 'ㇴ', 'ㇵ', 'ㇶ', 'ㇷ', 'ㇸ', 'ㇹ', 'ㇺ', 'ㇻ', 'ㇼ', 'ㇽ', 'ㇾ', 'ㇿ',
]);

function isSmallKana(char: string): boolean {
  return SMALL_KANA.has(char);
}

export class FontToDots {
  async textToDots(
    fontFamily: string,
    text: string,
    fontSize: number,
    threshold: number = 128,
    vertical: boolean = false,
    letterSpacing: number = 0
  ): Promise<DotsResult> {
    if (vertical) {
      return this.textToDotsVertical(fontFamily, text, fontSize, threshold);
    }
    return this.textToDotsHorizontal(fontFamily, text, fontSize, threshold, letterSpacing);
  }

  private async textToDotsHorizontal(
    fontFamily: string,
    text: string,
    fontSize: number,
    threshold: number,
    letterSpacing: number = 0
  ): Promise<DotsResult> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context not available');

    ctx.font = `${fontSize}px "${fontFamily}", sans-serif`;
    ctx.textBaseline = 'top';
    (ctx as any).letterSpacing = `${letterSpacing}px`;

    const metrics = ctx.measureText(text);
    const textWidth = Math.ceil(metrics.width) + (text.length - 1) * letterSpacing;
    const textHeight = Math.ceil(metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent);

    const padding = fontSize * 0.2;
    canvas.width = textWidth + padding * 2;
    canvas.height = textHeight + padding * 2;

    ctx.font = `${fontSize}px "${fontFamily}", sans-serif`;
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    (ctx as any).letterSpacing = `${letterSpacing}px`;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = '#000000';
    ctx.fillText(text, padding, padding);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    const dots: boolean[][] = [];

    for (let y = 0; y < canvas.height; y++) {
      dots[y] = [];
      for (let x = 0; x < canvas.width; x++) {
        const idx = (y * canvas.width + x) * 4;
        const brightness = (pixels[idx] + pixels[idx + 1] + pixels[idx + 2]) / 3;
        dots[y][x] = brightness < threshold;
      }
    }

    const trimmedDots = this.trimEmptySpace(dots);
    const brailleLines = this.dotsToBraille(trimmedDots);

    return {
      dots: trimmedDots,
      brailleLines,
      width: trimmedDots[0]?.length || 0,
      height: trimmedDots.length
    };
  }

  private trimEmptySpace(dots: boolean[][]): boolean[][] {
    if (dots.length === 0 || dots[0].length === 0) return dots;

    let minY = dots.length;
    let maxY = 0;
    let minX = dots[0].length;
    let maxX = 0;

    for (let y = 0; y < dots.length; y++) {
      for (let x = 0; x < dots[y].length; x++) {
        if (dots[y][x]) {
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
        }
      }
    }

    if (minY > maxY || minX > maxX) return dots;

    const trimmed: boolean[][] = [];
    for (let y = minY; y <= maxY; y++) {
      trimmed[y - minY] = [];
      for (let x = minX; x <= maxX; x++) {
        trimmed[y - minY][x - minX] = dots[y][x];
      }
    }

    return trimmed;
  }

  private async textToDotsVertical(
    fontFamily: string,
    text: string,
    fontSize: number,
    threshold: number
  ): Promise<DotsResult> {
    const brailleLines: string[] = [];
    const brailleColumns: string[][] = [];

    // 基準サイズを取得（「あ」などの通常文字のサイズを基準にする）
    const baseSize = await this.getBaseCharSize(fontFamily, fontSize);

    for (const char of text) {
      const isSmall = isSmallKana(char);
      const result = await this.renderCharWithFixedSize(
        fontFamily,
        char,
        fontSize,
        threshold,
        baseSize,
        isSmall,
        true // vertical mode
      );
      brailleLines.push(result.brailleLines.join('\n'));
      brailleColumns.push(result.brailleLines);
    }

    return {
      dots: [],
      brailleLines,
      brailleColumns,
      width: 0,
      height: 0
    };
  }

  // 基準文字のサイズを取得
  private async getBaseCharSize(fontFamily: string, fontSize: number): Promise<{ width: number; height: number }> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context not available');

    ctx.font = `${fontSize}px "${fontFamily}", sans-serif`;
    ctx.textBaseline = 'top';

    // 基準文字として「あ」を使用
    const baseChar = 'あ';
    const metrics = ctx.measureText(baseChar);
    const width = Math.ceil(metrics.width);
    const height = Math.ceil(metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent);

    return { width, height };
  }

  // 固定サイズで文字をレンダリング
  private async renderCharWithFixedSize(
    fontFamily: string,
    char: string,
    fontSize: number,
    threshold: number,
    baseSize: { width: number; height: number },
    isSmall: boolean,
    isVertical: boolean
  ): Promise<DotsResult> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context not available');

    const padding = fontSize * 0.2;
    // 基準サイズ + パディングでキャンバスサイズを固定
    canvas.width = baseSize.width + padding * 2;
    canvas.height = baseSize.height + padding * 2;

    ctx.font = `${fontSize}px "${fontFamily}", sans-serif`;
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 文字の実際のサイズを測定
    const metrics = ctx.measureText(char);
    const charWidth = Math.ceil(metrics.width);
    const charHeight = Math.ceil(metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent);

    ctx.fillStyle = '#000000';

    let x = padding;
    let y = padding;

    if (isSmall) {
      if (isVertical) {
        // 縦書きの場合：小書き文字は右上に配置
        x = padding + (baseSize.width - charWidth);
        y = padding;
      } else {
        // 横書きの場合：小書き文字は右下に配置
        x = padding + (baseSize.width - charWidth);
        y = padding + (baseSize.height - charHeight);
      }
    } else {
      // 通常文字は中央に配置
      x = padding + (baseSize.width - charWidth) / 2;
      y = padding + (baseSize.height - charHeight) / 2;
    }

    ctx.fillText(char, x, y);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    const dots: boolean[][] = [];

    for (let py = 0; py < canvas.height; py++) {
      dots[py] = [];
      for (let px = 0; px < canvas.width; px++) {
        const idx = (py * canvas.width + px) * 4;
        const brightness = (pixels[idx] + pixels[idx + 1] + pixels[idx + 2]) / 3;
        dots[py][px] = brightness < threshold;
      }
    }

    // 縦書きモードでは固定サイズを維持するため trimEmptySpace を呼ばない
    // 横方向のみトリム（左右の空白を削除）
    const trimmedDots = this.trimHorizontalSpace(dots);
    const brailleLines = this.dotsToBraille(trimmedDots);

    return {
      dots: trimmedDots,
      brailleLines,
      width: trimmedDots[0]?.length || 0,
      height: trimmedDots.length
    };
  }

  // 横方向のみ空白を削除（縦方向は維持）
  private trimHorizontalSpace(dots: boolean[][]): boolean[][] {
    if (dots.length === 0 || dots[0].length === 0) return dots;

    let minX = dots[0].length;
    let maxX = 0;

    for (let y = 0; y < dots.length; y++) {
      for (let x = 0; x < dots[y].length; x++) {
        if (dots[y][x]) {
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x);
        }
      }
    }

    if (minX > maxX) return dots;

    const trimmed: boolean[][] = [];
    for (let y = 0; y < dots.length; y++) {
      trimmed[y] = [];
      for (let x = minX; x <= maxX; x++) {
        trimmed[y][x - minX] = dots[y][x];
      }
    }

    return trimmed;
  }
  
  private dotsToBraille(dots: boolean[][]): string[] {
    const height = dots.length;
    const width = dots[0]?.length || 0;
    const lines: string[] = [];
    
    const blockHeight = 3;
    const blockWidth = 2;
    
    for (let y = 0; y < height; y += blockHeight) {
      let line = '';
      for (let x = 0; x < width; x += blockWidth) {
        let pattern = 0;
        
        if (y < height && x < width && dots[y][x]) pattern |= 1 << 0;
        if (y + 1 < height && x < width && dots[y + 1][x]) pattern |= 1 << 1;
        if (y + 2 < height && x < width && dots[y + 2][x]) pattern |= 1 << 2;
        if (y < height && x + 1 < width && dots[y][x + 1]) pattern |= 1 << 3;
        if (y + 1 < height && x + 1 < width && dots[y + 1][x + 1]) pattern |= 1 << 4;
        if (y + 2 < height && x + 1 < width && dots[y + 2][x + 1]) pattern |= 1 << 5;
        
        line += String.fromCharCode(0x2800 + pattern);
      }
      lines.push(line);
    }
    
    return lines;
  }
  
}

export const fontToDots = new FontToDots();
